import React, { useMemo } from 'react';
import ContainerSectionTitle from 'components/content/shared/other/ContainerSectionTitle';
import { Column } from 'react-table';
import SimpleDataTable from 'components/content/shared/tables/SimpleDataTable';
import { ITablePropertyExtensions } from 'types/components/react_table/IReactTablePropertyExtensions';
import ILookupModel from 'types/backend_models/lookup_status/ILookupModel';
import { formatLookupModal } from 'functions/formatters';
import { formatToEuro } from 'functions/formatters/currency';
import NoDataMessageContainer from 'components/content/shared/other/NoDataMessageContainer';
import { cnTblFootRow, cnTblFootCell, cnTblFootCellDense } from 'components/content/shared/tables/TableStyleConstants';
import { IDeliveryLine } from 'types/backend_models/deliveries/IDeliveryLine';
import { nameofFactory } from 'functions/misc/NameOf';
import { toPercentageString } from 'functions/formatters/numbers';
import { tooltipUoM, tooltipQtyDelivered, tooltipQtyPlanned } from 'components/content/shared/Tooltips';
import { toYearAndMonthDateBE } from 'functions/formatters/date';
import { IRoute, productsPageRoutes } from 'components/navigation/routes';
import { RouteId } from 'types/frontend_models/navigation/RouteId';
import { useHistory } from 'react-router-dom';

interface IProps
{
	deliveryLines: IDeliveryLine[],
	unitsOfMeasurement: ILookupModel[]
}

const containerTitle = 'Delivery Lines';

export function DeliveryDetailLinesContainer(props: IProps)
{
	const history = useHistory()


	const columns = useMemo(() =>
	{
		return createColumns(props.unitsOfMeasurement, history);
		// eslint-disable-next-line
	}, [props.unitsOfMeasurement]);
	const totalPrice = useMemo(() => getTotalPrice(props.deliveryLines), [props]);

	if (props.deliveryLines.length === 0) {
		return (
			<div>
				<ContainerSectionTitle>{containerTitle}</ContainerSectionTitle>
				<div className="h-200">
					<NoDataMessageContainer containerClassName="h100p" message="Delivery has no deliverylines." />
				</div>
			</div>
		)
	}

	const goToRoute = productsPageRoutes.find(r => r.routeId === RouteId.productDetail) as IRoute;
	const handleRowClick = (row: any) => {
		console.log(row)
		const id = row?.values?.id
		if (!id) return '';
		let path = goToRoute.path + '?id=' + id;
		history.push(path);
	}

	return (
		<div>
			<ContainerSectionTitle>{containerTitle}</ContainerSectionTitle>
			<div style={{ overflowX: 'auto' }}>
				<SimpleDataTable
					columns={columns}
					data={props.deliveryLines}
					propertyExtensions={TablepropsExtensions}
					hasRowClick={true}
					//@ts-ignore
					onRowClick={handleRowClick}
					footer={
						<tr className={cnTblFootRow} style={{ textAlign: 'right'}}>
							<td className={`${cnTblFootCell} ${cnTblFootCellDense}`} colSpan={columns.length}>
									<span style={{ marginRight: 35, fontWeight: 500 }}>Delivery Total</span>{formatToEuro(totalPrice)}
							</td>
						</tr>
					}
				/>
			</div>
		</div>
	);
};

function getTotalPrice(deliveryLines: IDeliveryLine[]): number
{
	let total = 0;
	deliveryLines.forEach(dl => total += dl.totalPrice);
	return total;
}

const deliveryNameOf = nameofFactory<IDeliveryLine>();

function createColumns(unitsOfMeasure: ILookupModel[], history: any): Column<Object>[]
{

	// const goToRoute = productsPageRoutes.find(r => r.routeId === RouteId.productDetail) as IRoute;

	// Column definitions for orders table
	const columns: Column<Object>[] = [

		{
			Header: 'Line Id',
			accessor: deliveryNameOf('id') as keyof Object,
			width: 30
		},
		{
			Header: "KEY REF",
			accessor: deliveryNameOf('keyRef') as keyof Object,
			maxWidth: undefined,
			width: 30
		},
		{
			Header: "Customer REF",
			accessor: deliveryNameOf('customerRef') as keyof Object,
			minWidth: undefined,
			maxWidth: undefined,
		},
		{
			Header: "Customer Description",
			accessor: deliveryNameOf('customerDescription') as keyof Object,
			minWidth: undefined,
			maxWidth: undefined,
		},
		{
			Header: "ISA REF",
			accessor: deliveryNameOf("isaRef") as keyof Object,
			maxWidth: undefined,
			width: 30,
			Cell: (props: { cell: { value: any; }; }) => <i style={{fontSize: 12 }}>{props.cell.value}</i>
		},
		{
			Header: "Lot Number",
			accessor: deliveryNameOf('lotNumber') as keyof Object,
			minWidth: undefined,
			maxWidth: undefined,
			width: 100,
		},
		{
			Header: "Expiry Date",
			accessor: deliveryNameOf('expiryDate') as keyof Object,
			minWidth: undefined,
			maxWidth: undefined,
			width: 100,
			Cell: (props: { cell: { value: { toString: () => any; }; }; }) =>
			{
				if (!props.cell.value) return '';
				const stringValue = props.cell.value.toString();
				return toYearAndMonthDateBE(stringValue);
			}
		},
		{
			Header: tooltipQtyPlanned(),
			accessor: deliveryNameOf('qtyPlanned') as keyof Object,
			maxWidth: undefined,
			width: 25
		},
		{
			Header: tooltipQtyDelivered(),
			accessor: deliveryNameOf('qtyDelivered') as keyof Object,
			maxWidth: undefined,
			width: 25
		},
		{
			Header: tooltipUoM(),
			accessor: deliveryNameOf('unitOfMeasureId') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: any; }; }) => formatLookupModal(unitsOfMeasure, Number(props.cell.value))
		},
		{
			Header: 'Unit Price',
			accessor: deliveryNameOf('unitPrice') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: any; }; }) => formatToEuro(Number(props.cell.value))
		},
		{
			Header: 'Discount',
			accessor: deliveryNameOf('discount') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: number; }; }) => toPercentageString(props.cell.value as number)
		},
		{
			Header: 'Total Price',
			accessor: deliveryNameOf('totalPrice') as keyof Object,
			maxWidth: undefined,
			width: 30,
			Cell: (props: { cell: { value: any; }; }) => formatToEuro(Number(props.cell.value))
		},
		// {
		// 	Header: 'Product',
		// 	// accessor: deliveryNameOf('nope') as keyof Object,
		// 	// maxWidth: 30,
		// 	width: 30,
		// 	Cell: (props: any) =>
		// 	{
		// 		const id = props?.row?.values?.id
		// 		if (!id) return '';

		// 		const handleClick = () => {
		// 			let path = goToRoute.path + '?id=' + id;
		// 			history.push(
		// 				path
		// 			);				
		// 		}

		// 		return (
		// 			<Button
		// 				size='small'
		// 				icon={<ExperimentOutlined />}
		// 				onClick={handleClick}
		// 				style={{marginLeft: 15}}
		// 			/>
		// 		)
		// 	}
		// }
	];

	return columns;
}

/** Used to apply additional column styling */
const TablepropsExtensions: ITablePropertyExtensions = {
	columnExtensions: [
		{ accessor: deliveryNameOf('qtyPlanned'), style: { textAlign: 'right' } },
		{ accessor: deliveryNameOf('qtyDelivered'), style: { textAlign: 'right' } },
		{ accessor: deliveryNameOf('unitOfMeasureId'), style: { textAlign: 'right' } },
		{ accessor: deliveryNameOf('unitPrice'), style: { textAlign: 'right' } },
		{ accessor: deliveryNameOf('discount'), style: { textAlign: 'right' } },
		{ accessor: deliveryNameOf('totalPrice'), style: { textAlign: 'right' } }
	],
}